"use client";

import {
  createContext,
  Dispatch,
  ReactNode,
  SetStateAction,
  useContext,
  useMemo,
  useState,
} from "react";
import moment from "moment";
import { gql, useSubscription } from "@apollo/client";
import {
  Exact,
  InputMaybe,
  Role,
  Scalars,
  UserWhereInput,
} from "@/gql/graphql";

const USERS = gql(`
  query ContextUsersStories(
    $where: UserWhereInput
    $take: Int
    $skip: Int
    $aggregateUserWhere2: UserWhereInput
  ) {
    users(where: $where, take: $take, skip: $skip) {
      id
      name
      lastName
      ProfilePhoto {
        url
      }
    }
    aggregateUser(where: $aggregateUserWhere2) {
      _count {
        _all
      }
    }
  }
`);

const STORY_SUBSCRIPTION = gql(`
    subscription NewStorySub {
      newStorySub {
        id
        User {
          id
          ProfilePhoto {
            url
          }
          country {
            id
          }
        }
      }
    }
  `);

type StoryContextType = {
  last24Hours: string;
  setCountryIds: Dispatch<SetStateAction<number[]>>;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  variables: any;
  haveUserStories: boolean;
  setHaveUserStories: Dispatch<SetStateAction<boolean>>;
};

const StoryContext = createContext<StoryContextType | undefined>(undefined);

export const StoryProvider = ({ children }: { children: ReactNode }) => {
  const [countryIds, setCountryIds] = useState<number[]>([]);
  const last24Hours = moment().subtract(24, "hours").toISOString();
  const sizeStory = 10;
  const [haveUserStories, setHaveUserStories] = useState(true);

  // variable for the query of StorySlide
  const variables: Exact<{
    where?: InputMaybe<UserWhereInput>;
    take?: InputMaybe<Scalars["Int"]["input"]>;
    skip?: InputMaybe<Scalars["Int"]["input"]>;
    aggregateUserWhere2?: InputMaybe<UserWhereInput>;
  }> = useMemo(
    () => ({
      aggregateUserWhere2: {
        role: { equals: Role.Craftman },
        UserStories: {
          some: {
            createdAt: { gte: last24Hours },
          },
        },
      },
      where: {
        role: { equals: Role.Craftman },
        UserStories: {
          some: {
            createdAt: { gte: last24Hours },
          },
        },
      },
      take: sizeStory,
      skip: 0,
    }),
    [last24Hours]
  );

  //   const [loadingMore, setLoadingMore] = useState(false);
  //   const [hasMore, setHasMore] = useState(true);

  //fetch more stories
  //   const handleFetchMore = useCallback(() => {
  //     if (loading || !hasMore) return;
  //     const currentLength = data?.users.length || 0;
  //     setLoadingMore(true);
  //     fetchMore({
  //       variables: { skip: currentLength, take: size },
  //       updateQuery: (prev, { fetchMoreResult }) => {
  //         if (!fetchMoreResult) return prev;
  //         return {
  //           ...fetchMoreResult,
  //           events: [...prev.users, ...fetchMoreResult.users],
  //         };
  //       },
  //     })
  //       .then(() => {
  //         setHasMore(
  //           currentLength + size < (data?.aggregateUser._count?._all || 0)
  //         );
  //       })
  //       .finally(() => setLoadingMore(false));
  //   }, [data, loading, fetchMore, hasMore, size]);

  //new story
  useSubscription(STORY_SUBSCRIPTION, {
    onData: ({ client, data }) => {
      const newStorySub = data.data?.newStorySub;
      if (!newStorySub) return;

      if (countryIds.length) {
        const countryId = newStorySub?.User?.country?.id;
        if (!countryId || (countryId && !countryIds.includes(countryId))) {
          return;
        }
      }

      try {
        // Read existing data from cache
        const existingData = client.readQuery({
          query: USERS,
          variables,
        });

        if (existingData?.users) {
          // Check if the user already exists in the cache
          const userExists = existingData.users.some(
            (user: { id: number }) => user?.id === newStorySub.User.id
          );

          if (!userExists) {
            // Write updated data back to the cache
            client.writeQuery({
              query: USERS,
              variables,
              data: {
                ...existingData,
                users: [
                  {
                    id: newStorySub.User.id,
                    ProfilePhoto: newStorySub.User.ProfilePhoto,
                  },
                  ...existingData.users,
                ],
              },
            });
          }
        }
      } catch (error) {
        console.error("Error updating cache:", error);
      }
    },
    onError: (err: unknown) => console.log("Subscription error:", err),
  });

  return (
    <StoryContext.Provider
      value={{
        last24Hours,
        setCountryIds,
        variables,
        haveUserStories,
        setHaveUserStories,
      }}
    >
      {children}
    </StoryContext.Provider>
  );
};

export const useStoryContext = () => {
  const context = useContext(StoryContext);
  if (!context) {
    throw new Error("useStoryContext must be used within a StoryProvider");
  }
  return context;
};
